import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { WizardStepDef } from "./steps";

// Horizontal top step indicator for the Import wizard. Completed steps show a
// check on a filled brand-green disc, the active step a green outline, upcoming
// steps a muted grey. Only the two navigation steps (merchant=0, mode=1) are
// click targets, and only once they sit behind the active step.
export function Stepper({
  steps,
  activeIndex,
  maxReached,
  onStepClick,
}: {
  steps: readonly WizardStepDef[];
  activeIndex: number;
  maxReached: number;
  onStepClick?: (index: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("import.steps.ariaLabel")} className="mb-8">
      <ol className="flex w-full items-center">
        {steps.map((s, i) => {
          const isDone = i < activeIndex;
          const isActive = i === activeIndex;
          const isVisited = i <= maxReached;
          const clickable = !!onStepClick && i <= 1 && isVisited && i < activeIndex;
          const last = i === steps.length - 1;
          return (
            <li key={s.key} className={`flex items-center ${last ? "" : "flex-1"}`}>
              <button
                type="button"
                disabled={!clickable}
                aria-current={isActive ? "step" : undefined}
                onClick={() => clickable && onStepClick?.(i)}
                className={`flex items-center gap-2.5 ${clickable ? "cursor-pointer" : "cursor-default"}`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-[13px] font-semibold transition-colors ${
                    isDone
                      ? "border-green bg-green text-white"
                      : isActive
                        ? "border-green text-green"
                        : "border-grey-200 text-grey-400"
                  }`}
                >
                  {isDone ? <Check size={16} strokeWidth={2.5} /> : i + 1}
                </span>
                <span
                  className={`hidden text-[13px] font-medium sm:inline ${
                    isActive ? "text-grey-800" : isDone ? "text-grey-600" : "text-grey-400"
                  }`}
                >
                  {t(s.labelKey)}
                </span>
              </button>
              {!last && (
                <span
                  className={`mx-3 h-0.5 flex-1 rounded-full transition-colors ${
                    i < activeIndex ? "bg-green" : "bg-grey-200"
                  }`}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
